// src/features/channels/components/ChannelsPage.tsx
//
// «Canales» (mockup 06) — route /channels, Dirección. Tables first, one
// chart per block at most (ground rule). `axis=arrival` by default.
import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useDataProvider, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { WidgetCard } from '@/components/common/WidgetCard';
import { ResponsiveMetricsTable, type MetricsColumn } from '@/components/common/ResponsiveMetricsTable';
import { StackedBarsChart } from '@/components/common/analytics/StackedBarsChart';
import { SampledFigure } from '@/components/common/analytics/SampledFigure';
import { useHotelScope } from '@/hooks/useHotelScope';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { hasResourcePermission } from '@/config/resourceConfig';
import { getChannelHex, getChannelMeta, getChannelShortLabelKey } from '@/design/registry/channelRegistry';
import { DASH, formatCount, formatPct, formatSeconds } from '@/lib/analyticsFormat';
import { getSectionLabel } from '@/design/registry/sectionRegistry';
import type { ChannelAnalytics, ChannelKeysEntry, ChannelStat } from '@/types/channelAnalytics';
import type { Channel } from '@/types/models';
import { TABLE_CHANNELS, channelRowLabelKey, stopMedian, visibleSectionChannels, visibleTableChannels } from '@/lib/channelsView';

/** Menu value of the «Todos» option; never stored in the selection. */
const ALL_CHANNELS = '__all__';

export const ChannelsPage: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const scope = useHotelScope();
  const exportCsv = useCsvExport({ page: 'channels' });
  const canExport = hasResourcePermission('channels', 'export');

  const [period, setPeriod] = useAnalyticsPeriod();
  const [axis, setAxis] = useState<'arrival' | 'activity'>('arrival');
  // Channels to show; empty = all. Applied on screen to every per-channel
  // block (the endpoint always returns every channel).
  const [channels, setChannels] = useState<Channel[]>([]);
  // «Por canal» also lists «Dispensador (solo llave)»; the section blocks only the channels with sections.
  const visibleChannels = visibleTableChannels(channels);
  const sectionChannels = visibleSectionChannels(channels);
  const isVisible = (channel: Channel) => visibleChannels.includes(channel);
  const { data, loading, error, refresh } = useAnalyticsFetch<ChannelAnalytics>(
    () => {
      const sp: any = scope.scopeParams;
      return (dataProvider as any).getChannelAnalytics(sp.hotelId, sp.groupId, period.from, period.to, sp.hotelIds, axis);
    },
    [dataProvider, scope.scopeParams, period.from, period.to, axis]
  );

  const handleExport = async () => {
    if (!data) return;
    await exportCsv(
      data.channels.filter((c) => isVisible(c.channel)),
      [
        { labelKey: 'resources.channelsAnalytics.table.columns.channel', value: (r: ChannelStat) => translate(channelRowLabelKey(r.channel)) },
        { labelKey: 'resources.channelsAnalytics.table.columns.journeys', value: (r: ChannelStat) => r.journeys },
        { labelKey: 'resources.channelsAnalytics.table.columns.completionRate', value: (r: ChannelStat) => r.completionRate ?? '' },
      ],
      { from: period.from, to: period.to }
    );
  };

  const channelColumns: MetricsColumn<ChannelStat>[] = [
    { key: 'channel', label: translate('resources.channelsAnalytics.table.columns.channel'), render: (row) => translate(channelRowLabelKey(row.channel)) },
    { key: 'journeys', label: translate('resources.channelsAnalytics.table.columns.journeys'), align: 'right', render: (row) => formatCount(row.journeys) },
    { key: 'completionRate', label: translate('resources.channelsAnalytics.table.columns.completionRate'), align: 'right', render: (row) => <SampledFigure value={row.completionRate} sample={row.journeys} minimum={data?.minimumSample} format={formatPct} /> },
    {
      key: 'medianTotalMinutes',
      label: translate('resources.channelsAnalytics.table.columns.medianTotalMinutes'),
      align: 'right',
      render: (row) => (
        <SampledFigure value={row.medianTotalMinutes} sample={row.durationSample} minimum={data?.minimumSample} format={(m) => (m != null ? formatSeconds(m * 60) : DASH)} />
      ),
    },
    { key: 'under2MinPct', label: translate('resources.channelsAnalytics.table.columns.under2MinPct'), align: 'right', render: (row) => <SampledFigure value={row.under2MinPct} sample={row.durationSample} minimum={data?.minimumSample} format={formatPct} /> },
    { key: 'under5MinPct', label: translate('resources.channelsAnalytics.table.columns.under5MinPct'), align: 'right', render: (row) => <SampledFigure value={row.under5MinPct} sample={row.durationSample} minimum={data?.minimumSample} format={formatPct} /> },
    { key: 'retried', label: translate('resources.channelsAnalytics.table.columns.retried'), align: 'right', render: (row) => formatCount(row.retried) },
  ];
  const channelRows = visibleChannels.map((ch) => data?.channels.find((c) => c.channel === ch)).filter((row): row is ChannelStat => Boolean(row));

  const keysColumns: MetricsColumn<ChannelKeysEntry>[] = [
    { key: 'where', label: translate('resources.channelsAnalytics.keys.columns.where'), render: (row) => translate(`resources.channelsAnalytics.keys.where${row.where}`) },
    { key: 'delivered', label: translate('resources.channelsAnalytics.keys.columns.delivered'), align: 'right', render: (row) => formatCount(row.delivered) },
    { key: 'card', label: translate('resources.channelsAnalytics.keys.columns.card'), align: 'right', render: (row) => formatCount(row.card) },
    { key: 'digital', label: translate('resources.channelsAnalytics.keys.columns.digital'), align: 'right', render: (row) => formatCount(row.digital) },
    { key: 'unknown', label: translate('resources.channelsAnalytics.keys.columns.unknown'), align: 'right', render: (row) => formatCount(row.unknown) },
    { key: 'failed', label: translate('resources.channelsAnalytics.keys.columns.failed'), align: 'right', render: (row) => formatCount(row.failed) },
  ];

  return (
    <Box>
      <SectionHeader manualScreen="channels"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <HotelScopeSelector scope={scope} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
            <TextField
              size="small"
              select
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (value) => {
                  const selected = value as Channel[];
                  return selected.length === 0
                    ? translate('common.all')
                    : selected.map((ch) => translate(getChannelMeta(ch).labelKey)).join(', ');
                },
              }}
              InputLabelProps={{ shrink: true }}
              label={translate('resources.channelsAnalytics.filters.channel')}
              value={channels}
              onChange={(e) => {
                // «Todos» clears the selection; picking a channel narrows it.
                const next = e.target.value as unknown as string[];
                setChannels(next.includes(ALL_CHANNELS) ? [] : (next as Channel[]));
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={ALL_CHANNELS} dense>
                <Checkbox size="small" checked={channels.length === 0} sx={{ p: 0.5, mr: 1 }} />
                <ListItemText primary={translate('common.all')} />
              </MenuItem>
              {TABLE_CHANNELS.map((ch) => (
                <MenuItem key={ch} value={ch} dense>
                  <Checkbox size="small" checked={channels.includes(ch)} sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={translate(getChannelMeta(ch).labelKey)} />
                </MenuItem>
              ))}
            </TextField>
            <ToggleButtonGroup size="small" exclusive value={axis} onChange={(_, v) => v && setAxis(v)}>
              <ToggleButton value="arrival" sx={{ textTransform: 'none', fontSize: 12, px: 1.25 }}>
                {translate('resources.channelsAnalytics.axis.arrival')}
              </ToggleButton>
              <ToggleButton value="activity" sx={{ textTransform: 'none', fontSize: 12, px: 1.25 }}>
                {translate('resources.channelsAnalytics.axis.activity')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
        subtitle={translate('resources.channelsAnalytics.subtitle')}
        helpConfig={{
          titleKey: 'resources.channelsAnalytics.help.title',
          descriptionKey: 'resources.channelsAnalytics.help.description',
          featureKeys: [
            'resources.channelsAnalytics.help.features.table',
            'resources.channelsAnalytics.help.features.stops',
            'resources.channelsAnalytics.help.features.sample',
          ],
          tipKeys: ['resources.channelsAnalytics.help.tips.channel', 'resources.channelsAnalytics.help.tips.axis'],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {error && <FetchErrorBanner onRetry={refresh} />}

      <WidgetCard title={translate('resources.channelsAnalytics.table.title')} description={translate('resources.channelsAnalytics.table.description')} sx={{ mb: 2.5 }}>
        <ResponsiveMetricsTable<ChannelStat> columns={channelColumns} rows={channelRows} getRowKey={(row) => row.channel} loading={loading} />
      </WidgetCard>

      <WidgetCard title={translate('resources.channelsAnalytics.chart.title')} sx={{ mb: 2.5 }}>
        <StackedBarsChart
          data={(data?.channels ?? []).filter((c) => isVisible(c.channel)).map((c) => ({ channel: translate(getChannelShortLabelKey(c.channel)), journeys: c.journeys, colorKey: c.channel }))}
          xKey="channel"
          series={[{ key: 'journeys', labelKey: 'resources.channelsAnalytics.table.columns.journeys', color: getChannelHex(null) }]}
          cellColor={(row) => getChannelHex(row.colorKey as Channel)}
          height={180}
        />
      </WidgetCard>

      <WidgetCard title={translate('resources.channelsAnalytics.keys.title')} description={translate('resources.channelsAnalytics.keys.description')} sx={{ mb: 2.5 }}>
        <ResponsiveMetricsTable<ChannelKeysEntry> columns={keysColumns} rows={data?.keys ?? []} getRowKey={(row) => row.where} loading={loading} />
      </WidgetCard>

      <WidgetCard title={translate('resources.channelsAnalytics.stops.title')} description={translate('resources.channelsAnalytics.stops.description')} sx={{ mb: 2.5 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('resources.channelsAnalytics.stops.columns.section')}</TableCell>
                {sectionChannels.map((ch) => (
                  <TableCell key={ch} align="right">{translate(getChannelShortLabelKey(ch))}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.stopsBySection ?? []).map((row) => (
                <TableRow key={row.section}>
                  <TableCell>{getSectionLabel(row.section, translate)}</TableCell>
                  {sectionChannels.map((ch) => {
                    const stat = row.byChannel[ch];
                    const median = stopMedian(stat, data?.minimumSample);
                    return (
                      <TableCell key={ch} align="right">
                        {stat && stat.reached > 0
                          ? `${formatCount(stat.stopped)}/${formatCount(stat.reached)} (${stat.pct != null ? formatPct(stat.pct) : translate('resources.channelsAnalytics.stops.insufficient')})`
                          : DASH}
                        {median.kind !== 'none' && (
                          <Typography component="div" variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {median.kind === 'value'
                              ? translate('resources.channelsAnalytics.stops.median', { time: formatSeconds(median.seconds) })
                              : translate('resources.channelsAnalytics.stops.medianInsufficient', { count: formatCount(median.count) })}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </WidgetCard>

      <WidgetCard title={translate('resources.channelsAnalytics.transitions.title')}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('resources.channelsAnalytics.transitions.columns.from')}</TableCell>
                <TableCell>{translate('resources.channelsAnalytics.transitions.columns.to')}</TableCell>
                <TableCell align="right">{translate('resources.channelsAnalytics.transitions.columns.journeys')}</TableCell>
                <TableCell align="right">{translate('resources.channelsAnalytics.transitions.columns.complete')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.transitions ?? []).filter((t) => isVisible(t.from) || isVisible(t.to)).map((t, i) => (
                <TableRow key={`${t.from}-${t.to}-${i}`}>
                  {/* Rows are keyed by (from, to, fromPartial): a half-done pre-check-in
                      says so in its «from», so two rows never read the same. */}
                  <TableCell>
                    {t.fromPartial
                      ? translate('resources.channelsAnalytics.transitions.fromPartial', { channel: translate(getChannelShortLabelKey(t.from)) })
                      : translate(getChannelShortLabelKey(t.from))}
                  </TableCell>
                  <TableCell>{translate(getChannelShortLabelKey(t.to))}</TableCell>
                  <TableCell align="right">{formatCount(t.journeys)}</TableCell>
                  <TableCell align="right">{formatCount(t.complete)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </WidgetCard>
    </Box>
  );
};

export default ChannelsPage;
